import Emitter from "es6-event-emitter";
import CookieManager from "./../utilities/cookiemanager";
import {
  createSubArray,
  generatePreview,
  getContent,
  generateCoverURL,
} from "./../utilities/utils";
import { PAGE_POSITION_TYPE } from "./../interfaces/page.interface";

const NUMBER_OF_RETRIES = 2;

export default class PubStore extends Emitter {
  _apiManager = null;
  contentManager = null;

  //contentPackageId
  cpID = null;
  //contentPackagePublicationId
  pubId = null;

  CPackage = null;

  PublicationToLoad = null;
  CPackagePages = null;

  options = null;

  constructor(
    APIManager,
    ContentManager,
    CPackage,
    contentPackageId,
    publicationId,
    cpVersion,
    appState,
    options
  ) {
    super();
    this._apiManager = APIManager;
    this.contentManager = ContentManager;
    this.CPackage = CPackage;
    this.cpID = contentPackageId;
    this.pubId = publicationId;
    this.cpVersion = cpVersion;
    this.options = options;
    this._appState = appState;
    this.setContentPackagePublication();

    /*
      Nextgen can be loaded before we initilize pubStore or it can be loaded later
    */
    this._appState.on(
      "nextgen:loaded",
      this._loadNextgenPublication.bind(this)
    );
  }

  loadPub() {
    if (this.isCurrentPublicationReplica()) {
      this._loadReplicaPublication();
    } else {
      this._loadNextgenPublication();
    }
  }

  setOriginalPageSize() {
    const promise = new Promise((resolve) => {
      const imageSrc = generatePreview(
        this.options.replicaImageSize,
        this.CPackagePages[0].PublicationPageID
      );
      // TODO use cover url, but then switching between long lasting supplements needs to have the correct contentPackageId
      // const imageSrc = generateCoverURL(this.cpID,this.options.namedSizes.covers.S, this.CPackagePages[0].PublicationPageID, this.cpVersion);
      const image = new Image();
      image.onload = () => {
        this.originalPageSize = {
          height: image.height,
          width: image.width,
        };
        resolve();
      };
      image.onerror = () => {
        this.ratio = this.options.ratio;
        resolve();
      };
      image.src = imageSrc;
    });
    return promise;
  }

  setRatio() {
    const promise = new Promise((resolve) => {
      // TODO use cover url, but then switching between long lasting supplements needs to have the correct contentPackageId
      const imageSrc = generateCoverURL(
        this.cpID,
        this.options.sizes.S,
        this.CPackagePages[0].PublicationPageID,
        this.cpVersion
      );
      const image = new Image();
      image.onload = () => {
        this.ratio = image.height / image.width;
        resolve();
      };
      image.onerror = () => {
        this.ratio = this.options.ratio;
        resolve();
      };
      image.src = imageSrc;
    });
    return promise;
  }

  reloadPub(publicationId, contentPackageId, version) {
    if (contentPackageId) this.cpID = contentPackageId;
    if (publicationId) this.pubId = publicationId;
    if (version) this.cpVersion = version;

    this.setContentPackagePublication();
    this.loadPub();
  }

  extractUniqueCategories() {
    return this.CPackagePages.reduce((acc, page, index) => {
      const categories = acc.map((c) => {
        return c.name;
      });

      if (
        page.PageCategory !== "" &&
        categories.indexOf(page.PageCategory) === -1
      ) {
        acc.push({
          name: page.PageCategory,
          page: index,
        });
      }
      return acc;
    }, []);
  }

  findPageByPageNumber(pageNumber) {
    for (let i = 0, len = this.CPackagePages.length; i < len; i++) {
      if (this.CPackagePages[i].PageNumber == pageNumber) {
        return this.CPackagePages[i];
      }
    }
  }

  /**
   * Returns object with current pages visible based on current page index
   * @param  {Number} currentPageIndex current page index
   * @return {Object} object with left and right page
   */
  getCurrentPages(currentPageIndex = 0) {
    if (!this.CPackagePages) return;
    const currentPage = this.CPackagePages[currentPageIndex];

    if (!this.isCurrentPublicationReplica()) return currentPage;

    const pages = {
      left: null,
      right: null,
    };
    if (currentPage) {
      switch (currentPage.PagePositionType) {
        case PAGE_POSITION_TYPE.FRONT:
          pages.right = currentPage;
          break;
        case PAGE_POSITION_TYPE.BACK:
          if (
            this.CPackagePages[currentPageIndex - 1] &&
            this.CPackagePages[currentPageIndex - 1].PagePositionType === "Left"
          ) {
            pages.right = currentPage;
            pages.left = this.CPackagePages[currentPageIndex - 1];
          } else {
            pages.left = currentPage;
          }
          break;
        case PAGE_POSITION_TYPE.LEFT:
          pages.left = currentPage;
          if (this._appState.isDoublePage)
            pages.right = this.CPackagePages[currentPageIndex + 1];
          break;
        default:
          pages.right = currentPage;
          if (this._appState.isDoublePage)
            pages.left = this.CPackagePages[currentPageIndex - 1];
      }
    }
    return pages;
  }

  getNewsPaper() {
    return this.CPackage.ContentPackagePublication.filter(
      (cp) =>
        cp.PublicationType.toLowerCase() == "main" &&
        cp.PublicationTitleFormat.toLowerCase() == "newspaper"
    )[0];
  }

  getNextgenPublication() {
    return this.CPackage.ContentPackagePublication.filter((cp) => {
      return (
        cp.PublicationType.toLowerCase() === "main" &&
        cp.PublicationTitleFormat.toLowerCase() === "tabletoptimized"
      );
    })[0];
  }

  isCurrentPublicationReplica() {
    return (
      this.PublicationToLoad.PublicationTitleFormat.toLowerCase() ===
      "newspaper"
    );
  }

  setContentPackagePublication() {
    if (this.pubId) {
      this.PublicationToLoad = this.CPackage.ContentPackagePublication.filter(
        (cp) => cp.PublicationID == this.pubId
      )[0];
    } else {
      this.PublicationToLoad = this.getPublicationByType(
        this._appState.preferredViewer
      );
      this.pubId = this.PublicationToLoad.PublicationID;
    }
    this._appState.isReplica = this.isCurrentPublicationReplica();
  }

  createSlides(singlePage) {
    return createSubArray(this.CPackagePages, singlePage ? 1 : 2);
  }

  /**
   * Loading pages from external reader application
   * @param  {Array} pages Pages collection
   */
  loadPagesFromReader(pages) {
    this.CPackagePages = pages;
    this._normalizeNextgen();
    this.trigger("pubStore:pubLoaded");
  }

  /**
   * Returns publication from content package by type
   * @param  {String} type 'nextgen' or 'replica'
   * @return {Object}      publication
   */
  getPublicationByType(type) {
    let publication = null;

    if (type === "nextgen") {
      publication = this.getNextgenPublication() || this.getNewsPaper();
    } else {
      publication = this.getNewsPaper() || this.getNextgenPublication();
    }

    if (!publication) throw new Error("Publication not found");

    return publication;
  }

  /**
   * Checks if current publication is main
   * @return {Boolean}
   */
  isCurrentPublicationMain() {
    return this.PublicationToLoad.PublicationType.toLowerCase() === "main";
  }

  /**
   * Checks if content package contains replica and nextgen
   * @return {boolean}
   */
  containsReplicaAndNextgen() {
    return (
      this.CPackage.ContentPackagePublication.filter((cp) => {
        return cp.PublicationType === "Main";
      }).length === 2
    );
  }

  ////////

  _loadReplicaPublication(numberOfTries = 1) {
    const promises = [];
    if (this._appState.newDownloaderFallbackMode) {
      promises.push(
        this.contentManager.getPublicationPages(
          this.cpID,
          this.pubId,
          this.cpVersion
        )
      );
      promises.push(
        this.contentManager.getPublicationPageContent(
          this.cpID,
          this.pubId,
          this.cpVersion
        )
      );
      promises.push(
        this.contentManager.getPublicationContentItems(
          this.cpID,
          this.pubId,
          this.cpVersion
        )
      );
      promises.push(
        this.contentManager.getPublicationPageContentItems(
          this.cpID,
          this.pubId,
          this.cpVersion
        )
      );
    } else {
      promises.push(
        this.contentManager.requestJson(
          this._appState.cdnAssetsDownloader.getJsonUrl(
            "GetPublicationPages",
            this.pubId
          )
        )
      );
      promises.push(
        this.contentManager.requestJson(
          this._appState.cdnAssetsDownloader.getJsonUrl(
            "GetPublicationPageContent",
            this.pubId
          )
        )
      );
      promises.push(
        this.contentManager.requestJson(
          this._appState.cdnAssetsDownloader.getJsonUrl(
            "GetPublicationContentItems",
            this.pubId
          )
        )
      );
      promises.push(
        this.contentManager.requestJson(
          this._appState.cdnAssetsDownloader.getJsonUrl(
            "GetPublicationPageContentItems",
            this.pubId
          )
        )
      );
    }
    Promise.all(promises)
      .then((data) => {
        this.CPackagePages = data[0].Page.map((page) => ({
          ...page,
          PageWidth: this.options.pageWidth,
          PageHeight: this.options.pageHeight,
          ActivePageArea: this.options.activePageArea,
          content: data[1].PublicationPage.filter(
            (pc) => pc.PublicationPageID == page.PublicationPageID
          ).map((pc) => {
            const { ContentItem, Category } = data[2].Content.filter(
              (ci) => ci.ContentID == pc.ContentID
            )[0];

            return {
              ...pc,
              defaultPercentageInView: Math.min(
                100,
                Math.max(
                  1,
                  Math.round(
                    (pc.Height * pc.Width * 100) / this.options.activePageArea
                  )
                )
              ),
              contentItem: ContentItem,
              contentItemContent: this._getContent(
                ContentItem,
                data[3].PublicationPages.filter((pp) => {
                  return pp.ContentID == pc.ContentID;
                })[0],
                this.options.dropCapIfFirstBold,
                Category
              ),
            };
          }),
        }));
        this.setOriginalPageSize().then(() => {
          this.setRatio().then(() => {
            this.trigger("pubStore:pubLoaded");
          });
        });
      })
      .catch((err) => {
        if (err === 403) {
          if (numberOfTries < NUMBER_OF_RETRIES) {
            this._appState.cdnAssetsDownloader
              .requestDownloadCredentials()
              .then(() => {
                this._loadReplicaPublication(numberOfTries + 1);
              });
          } else {
            this._appState.newDownloaderFallbackMode = true;
            this._loadReplicaPublication(numberOfTries + 1);
          }
        }
      });
  }

  _normalizeNextgen() {
    this.CPackagePages.forEach((page) => {
      if (page.PublicationPageId) {
        page.PublicationPageID = page.PublicationPageId;
      }
      if (page.PageNumber && !page.PageLabel) {
        page.PageLabel = page.PageNumber;
      }
    });
  }

  /**
   * For Nextgen - pages are loaded and parsed in nextgen application.
   * Webapp only initialize Nextgen app and bridge between two apps
   */
  _loadNextgenPublication() {
    if (!window.app) return; // nextgen is not loaded, we must wait

    let packageDir;

    if (process.env.NODE_ENV === "production") {
      packageDir = "../";
    } else {
      packageDir = `../reader/data/${this.CPackage.ContentPackageID}/`;
    }

    window.app.init({
      pub: this.PublicationToLoad.PublicationID,
      packageId: this.CPackage.ContentPackageID,
      appmode: this._appState.isDesktop ? "webappDesktop" : "webapp",
      isPhone: !this._appState.isTablet && !this._appState.isDesktop,
      packageDir: `${packageDir}`,
      prefetchPages: true,
      articleId: this._appState.nextgenSharingArticle,
      page: this._appState.currentPage,
    });
  }

  _getContent(contentItems, publicationPage, changeDropCap, category = "") {
    let content;
    return () => {
      if (content) return content;
      const { sessionId, userId, downloadId } = this._appState;
      content = getContent(
        contentItems,
        publicationPage,
        changeDropCap,
        sessionId,
        userId,
        downloadId,
        category
      );
      return content;
    };
  }

  _getReaderSessionData() {
    const readerSessionCookie = CookieManager.getCookie(
      CookieManager.cookieKeys.READER_SESSION
    );
    if (readerSessionCookie) {
      return JSON.parse(readerSessionCookie);
    }
    return {};
  }
}



// WEBPACK FOOTER //
// ./src/common/stores/pubStore.js